import { ACCENT, ACCENT_BRIGHT, ACCENT_HI, ACCENT_DEEP } from '../../lib/medievalTheme';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { motion, useReducedMotion } from 'framer-motion';

/**
 * One travelling indicator shared by every tab bar in the app.
 *
 * The indicator is a single absolutely-positioned element that springs
 * between tabs instead of each tab fading its own background in and out.
 * That is the whole micro-interaction: the selection is one object that
 * moves, so the eye tracks it from the old tab to the new one.
 *
 * Position comes from `offsetLeft` / `offsetWidth`, not
 * `getBoundingClientRect()`. Tab bars live inside `.app-canvas`, and the
 * leaderboard rails additionally wear `transform: scale(var(--canvas-scale))`.
 * Rect values are in the scaled viewport space; offsets are in the same
 * untransformed layout space the indicator itself is positioned in, so
 * they stay correct at every UI scale. See CLAUDE.md, "UI Scaling".
 */

// Low bounce: enough life that a rapid back-and-forth feels springy,
// not so much that the indicator wobbles under a settled selection.
const SLIDE = { type: 'spring', duration: 0.35, bounce: 0.18 };

const GOLD_BAR = `linear-gradient(90deg, transparent 0%, ${ACCENT_BRIGHT} 0.95) 50%, transparent 100%)`;

// Each preset reproduces the active-state chrome its call sites used to
// paint on the button itself, so converting a tab bar changes how the
// selection moves without changing how it looks.
const INDICATOR = {
  // shadcn-style gold pill in a dark trough (TAB_ACTIVE).
  pill: {
    background: `${ACCENT} 0.15)`,
    borderRadius: 'var(--radius-md)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04)',
  },
  // Lit brass plate in a recessed groove (ArenaStore / ArenaHub).
  plate: {
    background: `url("/tex-noise.webp"), linear-gradient(180deg, ${ACCENT_BRIGHT} 0.15) 0%, ${ACCENT} 0.08) 50%, ${ACCENT_DEEP} 0.11) 100%)`,
    border: `1px solid ${ACCENT} 0.35)`,
    borderRadius: 'var(--radius-sm)',
    boxShadow: `inset 0 1px 0 ${ACCENT_HI} 0.14), inset 0 -1px 0 rgba(0,0,0,0.28), 0 0 12px ${ACCENT_BRIGHT} 0.13)`,
  },
  // Glowing bar under the label; the box itself stays invisible.
  underline: { background: 'transparent' },
  // Underline plus the warm wash the chapter rails put behind the label.
  chapter: {
    background: `linear-gradient(180deg, ${ACCENT_BRIGHT} 0.14) 0%, ${ACCENT_DEEP} 0.08) 100%)`,
    boxShadow: `inset 0 0.0625cqw 0 ${ACCENT_HI} 0.12), inset 0 0 1.5cqw ${ACCENT} 0.08)`,
  },
};

// Walk the offsetParent chain so a tab wrapped in a tooltip or a grid
// cell still measures relative to the bar. The container is always
// positioned, so it terminates the walk.
function measure(el, container) {
  let left = 0;
  let top = 0;
  for (let node = el; node && node !== container; node = node.offsetParent) {
    left += node.offsetLeft;
    top += node.offsetTop;
  }
  return { left, top, width: el.offsetWidth, height: el.offsetHeight };
}

function same(a, b) {
  return a && b && a.left === b.left && a.top === b.top && a.width === b.width && a.height === b.height;
}

/**
 * @param {number} activeIndex Index into the `[data-tab-item]` children. -1 hides the indicator.
 * @param {'pill'|'plate'|'underline'|'chapter'} variant Which active-state chrome the indicator paints.
 * @param {object} [indicatorStyle] Per-call-site overrides merged over the preset.
 * @param {string} [barInset] Horizontal inset of the underline bar, for the underline/chapter variants.
 * @param {object} [barStyle] Overrides merged over the underline bar.
 */
export function TabBar({
  activeIndex,
  variant = 'pill',
  indicatorStyle,
  barInset = '10%',
  barStyle,
  children,
  className = '',
  style,
  ...props
}) {
  const containerRef = useRef(null);
  const [box, setBox] = useState(null);
  const reduce = useReducedMotion();

  const readRef = useRef(null);
  readRef.current = () => {
    const container = containerRef.current;
    if (!container) return;
    const items = container.querySelectorAll('[data-tab-item]');
    const el = activeIndex >= 0 ? items[activeIndex] : null;
    // Marks the selected tab for the shared hover rule in index.css, so
    // call sites don't each have to restate which tab is active in a
    // second attribute. Preact leaves attributes it doesn't own alone.
    items.forEach((item) => {
      if (item === el) item.setAttribute('data-tab-active', '');
      else item.removeAttribute('data-tab-active');
    });
    if (!el) {
      setBox(null);
      return;
    }
    const next = measure(el, container);
    setBox((prev) => (same(prev, next) ? prev : next));
  };

  // Re-read on every render: cheap (offsets on one element) and it covers
  // the selection moving, a tab gaining a count badge, and tabs being
  // added or removed, without a dependency list that can go stale.
  useLayoutEffect(() => readRef.current());

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    // Window resizes and the UI-scale slider change the bar's width
    // without re-rendering it.
    const observer = new ResizeObserver(() => readRef.current());
    observer.observe(container);
    // The medieval display faces load late; labels reflow when they land.
    document.fonts?.ready.then(() => readRef.current());
    return () => observer.disconnect();
  }, []);

  const bar = variant === 'underline' || variant === 'chapter';

  return (
    <div
      ref={containerRef}
      className={`vh-tabbar ${className}`}
      style={{ position: 'relative', ...style }}
      {...props}
    >
      {box ? (
        <motion.div
          aria-hidden="true"
          initial={false}
          animate={{
            transform: `translate3d(${box.left}px, ${box.top}px, 0)`,
            width: box.width,
            height: box.height,
          }}
          transition={reduce ? { duration: 0 } : SLIDE}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            boxSizing: 'border-box',
            pointerEvents: 'none',
            ...INDICATOR[variant],
            ...indicatorStyle,
          }}
        >
          {bar ? (
            <span
              style={{
                position: 'absolute',
                left: barInset,
                right: barInset,
                bottom: 0,
                height: '0.125cqw',
                background: GOLD_BAR,
                boxShadow: `0 0 0.625cqw ${ACCENT_BRIGHT} 0.7)`,
                ...barStyle,
              }}
            />
          ) : null}
        </motion.div>
      ) : null}
      {children}
    </div>
  );
}

export default TabBar;
